import type { UpdaterEvent } from '../types/modpackUpdater';

export interface DeepCleanDimension {
  dimension: string; folder: string; policy: 'keep' | 'wipe' | null; mapped: boolean;
  regions: number; protected: number; delete: number; delete_bytes: number;
  reasons: Record<string, number>; wipe_overrides: Record<string, number>;
  map: { cells: { x: number; z: number; kept: boolean }[]; total: number; sampled: boolean };
}
export interface DeepCleanPlan {
  schema: 1;
  settings: { active_days: number };
  totals: { regions: number; protected: number; delete_regions: number; delete_files: number; delete_bytes: number; players: number; active_players: number; relocations: number; kept_bytes: number };
  dimensions: DeepCleanDimension[];
  dimension_count: number;
  backup: null | { path: string; exists: boolean; newest: number | null; age: number | null; count: number };
  minecraft: { name: string; data_version: number };
  protection?: { world_layout: 'legacy' | 'namespaced'; claim_providers: string[]; claim_groups: number; claimed_chunks: number; server_claim_groups: number };
  sensitivity: { days: number; active_players: number; protected: number; delete_regions: number; bytes: number; relocations: number }[];
  warnings: string[]; warning_count: number;
  refusals: { id: string; detail: string; override: null }[]; refusal_count: number;
  relocation_names: string[];
  report_path: string | null;
}

/** Charts display the machine's report. They never produce or narrow the approved deletion plan. */
export function latestDeepCleanPlan(events: UpdaterEvent[]): DeepCleanPlan | null {
  const value = [...events].reverse().find(event => event.type === 'log' && event.payload.deepclean_plan)?.payload.deepclean_plan;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const plan = value as DeepCleanPlan;
  const count = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  if (plan.schema !== 1 || !plan.settings || !count(plan.settings.active_days) || !plan.totals
    || !['regions', 'protected', 'delete_regions', 'delete_files', 'delete_bytes', 'players', 'active_players', 'relocations', 'kept_bytes'].every(key => count(plan.totals[key as keyof typeof plan.totals]))
    || !Array.isArray(plan.dimensions) || plan.dimensions.length > 128 || !count(plan.dimension_count)
    || !Array.isArray(plan.warnings) || !plan.warnings.every(value => typeof value === 'string')
    || !Array.isArray(plan.refusals) || !plan.refusals.every(value => value && typeof value.id === 'string' && typeof value.detail === 'string')
    || !Array.isArray(plan.relocation_names) || !plan.relocation_names.every(value => typeof value === 'string')
    || !Array.isArray(plan.sensitivity) || !plan.sensitivity.every(row => row && ['days', 'active_players', 'protected', 'delete_regions', 'bytes', 'relocations'].every(key => count(row[key as keyof typeof row])))
    || !count(plan.warning_count) || !count(plan.refusal_count)) return null;
  let cells = 0;
  for (const dim of plan.dimensions) {
    if (!dim || typeof dim.dimension !== 'string' || typeof dim.folder !== 'string' || typeof dim.mapped !== 'boolean'
      || ![null, 'keep', 'wipe'].includes(dim.policy) || !['regions', 'protected', 'delete', 'delete_bytes'].every(key => count(dim[key as 'regions']))
      || !dim.reasons || !dim.wipe_overrides || !Object.values(dim.reasons).every(count) || !Object.values(dim.wipe_overrides).every(count)
      || !dim.map || !count(dim.map.total) || typeof dim.map.sampled !== 'boolean' || !Array.isArray(dim.map.cells)
      || !dim.map.cells.every(cell => cell && Number.isSafeInteger(cell.x) && Number.isSafeInteger(cell.z) && typeof cell.kept === 'boolean')) return null;
    cells += dim.map.cells.length;
  }
  if (cells > 4096) return null;
  if (plan.protection && (!['legacy', 'namespaced'].includes(plan.protection.world_layout)
    || !Array.isArray(plan.protection.claim_providers) || plan.protection.claim_providers.length > 16
    || !plan.protection.claim_providers.every(value => typeof value === 'string' && value.length > 0 && value.length <= 100)
    || !['claim_groups', 'claimed_chunks', 'server_claim_groups'].every(key => count(plan.protection![key as 'claim_groups'])))) return null;
  if (plan.backup && (typeof plan.backup.path !== 'string' || typeof plan.backup.exists !== 'boolean' || !count(plan.backup.count)
    || plan.backup.newest !== null && !count(plan.backup.newest) || plan.backup.age !== null && !count(plan.backup.age))) return null;
  if (plan.report_path !== null && typeof plan.report_path !== 'string') return null;
  return plan;
}

export function cleanupBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toLocaleString(undefined, { maximumFractionDigits: index ? 1 : 0 })} ${units[index]}`;
}
